/**
 * EmbedView — strict read-only post view designed for inline-iframe usage.
 *
 * Auth model (CloudV2 Draft-Support):
 *  1. On mount the iframe sends `{type: 'embed-ready'}` to window.parent.
 *  2. Parent (cloud2.arkturian.com / cloud.arkserver.arkturian.com) replies
 *     with `{type: 'embed-auth', token: <jwt>}` if the user is logged in.
 *  3. We hold the token in component state only — NOT localStorage — and
 *     pass it as Authorization: Bearer on every API call from inside the
 *     iframe. Origin of incoming postMessage is whitelisted.
 *  4. If no token arrives within 300ms the embed simply fetches anonymously,
 *     which keeps published-post embeds in public chats working.
 *
 * Visual:
 *  - cover_image_url / first unreferenced media → hero
 *  - remaining unreferenced media → 60x60 thumbnail strip
 *  - body via parseMarkdown (Mermaid, code, etc.)
 *  - all body links forced to target=_blank so iframe stays put
 */

import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { parseMarkdown } from '../utils/markdownConfig'
import { Presentation3DViewer } from '../components/Presentation3DViewer'
import { CONTENT_API, PRESENTER_3D_ENABLED } from '../config'
import type { MediaItem, Post } from '../types'
import '../styles/global.css'

// Origins that are trusted to provide auth tokens for embedded posts.
const TRUSTED_PARENT_ORIGINS = [
  'https://cloud2.arkturian.com',
  'https://cloud.arkturian.com',
  'https://cloud.arkserver.arkturian.com',
  'https://cloud2.arkserver.arkturian.com',
  'https://cloud2.mac.arkturian.com',
  'https://cloud.mac.arkturian.com',
  'https://content2.arkturian.com',
  'https://content.arkturian.com',
]

const API_BASE = CONTENT_API + '/api/v1'

interface EmbedAuthMessage {
  type: 'embed-auth'
  token: string | null
}

function isEmbedAuth(data: unknown): data is EmbedAuthMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as { type?: unknown }).type === 'embed-auth'
  )
}

async function fetchPost(id: string, token: string | null): Promise<Post> {
  // Strict tenant isolation can make a published post invisible to a
  // logged-in caller from a different tenant (or to a tenant-member
  // looking at a partner_id=NULL legacy post). For an embed that's
  // useless — the post may be public to anonymous viewers but invisible
  // to the authed iframe. So: if the authed attempt comes back 401/404,
  // fall through to anonymous fetch. Anonymous itself only sees
  // status=published posts, so this can't leak anything beyond what a
  // public visitor of /share/:id or a curl already sees.
  if (token) {
    const res = await fetch(`${API_BASE}/posts/${id}`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
    if (res.ok) return res.json() as Promise<Post>
    if (res.status !== 401 && res.status !== 404) {
      throw new Error(`HTTP ${res.status}`)
    }
    // 401/404 with token → try anonymous below
  }
  const res = await fetch(`${API_BASE}/posts/${id}`, {
    headers: { 'Content-Type': 'application/json' },
  })
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`)
  }
  return res.json() as Promise<Post>
}

export function EmbedView() {
  const { id } = useParams<{ id: string }>()
  const contentRef = useRef<HTMLDivElement>(null)

  // Token from postMessage handshake. null until first message (or fallback).
  const [embedToken, setEmbedToken] = useState<string | null>(null)
  const [handshakeDone, setHandshakeDone] = useState(false)

  // ── postMessage handshake ─────────────────────────────────────
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null

    function onMessage(event: MessageEvent) {
      if (!TRUSTED_PARENT_ORIGINS.includes(event.origin)) return
      if (!isEmbedAuth(event.data)) return
      setEmbedToken(event.data.token ?? null)
      setHandshakeDone(true)
      if (timer) { clearTimeout(timer); timer = null }
    }

    window.addEventListener('message', onMessage)

    // Tell the parent we are ready to receive auth.
    if (window.parent && window.parent !== window) {
      try {
        window.parent.postMessage({ type: 'embed-ready' }, '*')
      } catch { /* parent on different origin and refused — ignore */ }
    } else {
      // We are NOT in an iframe → no handshake possible, proceed anon.
      setHandshakeDone(true)
    }

    // Fall back to anonymous after 300ms if no parent response.
    timer = setTimeout(() => setHandshakeDone(true), 300)

    return () => {
      window.removeEventListener('message', onMessage)
      if (timer) clearTimeout(timer)
    }
  }, [])

  // ── Fetch post once handshake settled ─────────────────────────
  const { data: post, isLoading, error } = useQuery({
    queryKey: ['embed-post', id, embedToken],
    queryFn: () => fetchPost(id!, embedToken),
    enabled: !!id && handshakeDone,
  })

  // Sort media: hero role first, then by position. Drop items that are
  // already referenced inline in the markdown to avoid double rendering.
  const unreferencedMedia = useMemo<MediaItem[]>(() => {
    if (!post?.media || post.media.length === 0) return []
    const sorted = [...post.media].sort((a, b) => {
      if (a.role === 'hero' && b.role !== 'hero') return -1
      if (b.role === 'hero' && a.role !== 'hero') return 1
      return (a.position ?? 0) - (b.position ?? 0)
    })
    const content = post.content || ''
    return sorted.filter(m => {
      if (m.media_url && content.includes(m.media_url)) return false
      if (m.storage_id && content.includes(`/media/${m.storage_id}`)) return false
      return true
    })
  }, [post?.media, post?.content])

  const heroSrc = post?.cover_image_url || unreferencedMedia[0]?.media_url || null
  const stripItems = post?.cover_image_url ? unreferencedMedia : unreferencedMedia.slice(1)

  // Force all body links into a new tab so clicks don't break the iframe.
  useEffect(() => {
    if (!contentRef.current) return
    contentRef.current.querySelectorAll('a').forEach(a => {
      if (!a.target || a.target === '_self') {
        a.target = '_blank'
        a.rel = 'noopener noreferrer'
      }
    })
  }, [post])

  if (!handshakeDone || isLoading) {
    return <div className="embed-state">Lade…</div>
  }
  if (error || !post) {
    return <div className="embed-state embed-state--error">Beitrag nicht gefunden.</div>
  }

  const html = parseMarkdown(post.content || '')

  // Special doc-type: 3D presentation → render the 3DPresenter iframe
  // full-bleed inside the embed instead of the markdown source.
  if (post.doc_type === '3dpresentation' || post.doc_type === '3d_presentation') {
    return (
      <div className="embed-wrap">
        {PRESENTER_3D_ENABLED && <Presentation3DViewer post={post} compact />}
      </div>
    )
  }

  return (
    <div className="embed-wrap">
      <div className="embed-header">
        <span className="embed-title" title={post.title}>{post.title}</span>
        <a
          className="embed-open"
          href={`/post/${post.id}`}
          target="_top"
          title="Im Content-Portal öffnen"
        >
          ↗
        </a>
      </div>
      <div
        ref={contentRef}
        className="embed-content editor-content"
      >
        {heroSrc && (
          <img className="embed-hero" src={heroSrc} alt="" loading="lazy" />
        )}
        {stripItems.length > 0 && (
          <div className="embed-strip">
            {stripItems.slice(0, 6).map(m => (
              <img
                key={m.id ?? m.storage_id}
                src={m.media_url}
                alt={m.caption || ''}
                loading="lazy"
              />
            ))}
          </div>
        )}
        <div dangerouslySetInnerHTML={{ __html: html }} />
      </div>
    </div>
  )
}
